/**
 * All page copy lives here.
 *
 * Transcribed verbatim from .reference/PortfolioPage.dc.html. Editing the
 * portfolio should mean editing this file and nothing else — the sections
 * map over these arrays, so adding a third experience entry or a fifth
 * skill tile is one object literal, no JSX.
 *
 * Voice rules (see .reference/DESIGN-SYSTEM.md): first person singular,
 * sentence case, numbers carry the argument and keep their `+` suffix
 * verbatim, metadata is middle-dot separated, no emoji, no exclamation marks.
 */

import type { IconName } from "@/ds/icons";
import { projects, projectHref } from "./projects";

/* ---------------------------------------------------------------- profile */

export const profile = {
  wordmark: "Mafazi",
  fullName: "Mafazi Ikhwan Dhandi Hibatullah",
  email: "mafazi123fafa@gmail.com",
  linkedinUrl: "https://www.linkedin.com/in/mafazi-ikhwan-dhandi-hibatullah-356b82225",
  location: "Bandung, Indonesia",
} as const;

/* -------------------------------------------------------------------- nav */

export type NavLink = { label: string; href: string };

/**
 * Root-relative rather than bare fragments: the same header renders on the case
 * study pages, where "#work" would point at a section that does not exist there
 * and the link would simply do nothing.
 */
export const navLinks: NavLink[] = [
  { label: "Work", href: "/#work" },
  { label: "Skills", href: "/#skills" },
  { label: "Contact", href: "/#contact" },
];

/* ------------------------------------------------------------------- hero */

/** Rotated in the "Currently" slot. Swap interval is 3200ms, fade 260ms. */
export const roles: string[] = [
  "Industrial Engineering Undergraduate, ITB",
  "HSSE&Q Intern, Pertamina Drilling Services",
  "Laboratory Assistant, LRSKE ITB",
];

export const hero = {
  eyebrow: "01 — Who I am",
  /** TODO: Unsplash placeholder — swap for a real photograph. */
  image: "https://images.unsplash.com/photo-1516937941344-00b4e0337589?w=1800&auto=format&fit=crop",
  imageAlt: "Industrial site at dusk",
  stat: {
    value: "30+",
    label: ["Job Safety Analysis", "documents reviewed"],
  },
  headline: "Systems work better when you measure the people in them.",
  paragraphs: [
    "I am a third-year Industrial Engineering student at Institut Teknologi Bandung with a strong interest in process optimization, data analytics, ergonomics, and occupational safety. I enjoy solving complex problems through analytical thinking and data-driven decision making.",
    "Laboratory instruction, student leadership, business case competitions, and industrial safety research have strengthened my skills in project management, collaboration, and continuous improvement while working in diverse teams.",
  ],
  currentlyLabel: "Currently",
  availability: "Open to new projects.",
  ctaLabel: "GET IN TOUCH",
} as const;

/* ------------------------------------------------------------- experience */

export type Experience = {
  index: string;
  title: string;
  src: string;
  alt: string;
  meta: string;
  href: string;
};

export const experienceSection = {
  eyebrow: "02 — Selected experience",
  headline: "Safety research, ergonomics instruction, and content that reaches people.",
} as const;

/** Card view of the case studies — the detail pages own the full content. */
export const experience: Experience[] = projects.map((p) => ({
  index: p.index,
  title: p.cardTitle,
  src: p.src,
  alt: p.alt,
  meta: p.meta,
  href: projectHref(p),
}));

/* ----------------------------------------------------------------- skills */

export type Skill = {
  icon: IconName;
  title: string;
  description: string;
  src: string;
};

export const skillsSection = {
  eyebrow: "03 — What I work with",
  headline: "Process optimization, data analytics, ergonomics, occupational safety.",
  toolsLabel: "Tools",
} as const;

export const skills: Skill[] = [
  {
    icon: "workflow",
    title: "Process optimization",
    description: "Mapping work systems, removing waste, and standardising the steps that stay.",
    src: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=900&auto=format&fit=crop",
  },
  {
    icon: "trending-up",
    title: "Data analytics",
    description: "Turning interviews, incident logs, and survey data into decisions people act on.",
    src: "https://images.unsplash.com/photo-1511818966892-d7d671e672a2?w=900&auto=format&fit=crop",
  },
  {
    icon: "accessibility",
    title: "Ergonomics",
    description: "Work systems measured around the operator, taught in the LRSKE ITB laboratory.",
    src: "https://images.unsplash.com/photo-1518005020951-eccb494ad742?w=900&auto=format&fit=crop",
  },
  {
    icon: "hard-hat",
    title: "Occupational safety",
    description: "Job Safety Analysis, safety climate research, and human factors on drilling sites.",
    src: "https://images.unsplash.com/photo-1449157291145-7efd050a4d0e?w=900&auto=format&fit=crop",
  },
];

export type Tool = { icon: IconName; label: string };

export const tools: Tool[] = [
  { icon: "figma", label: "Figma" },
  { icon: "palette", label: "Canva" },
  { icon: "box", label: "SOLIDWORKS" },
  { icon: "clipboard-check", label: "Job Safety Analysis" },
  { icon: "messages-square", label: "Focus group discussions" },
  { icon: "refresh-cw", label: "Continuous improvement" },
];

/* ---------------------------------------------------------------- contact */

export type ContactRow = { label: string; value: string; href?: string };

export const contactSection = {
  eyebrow: "04 — Contact",
  headline: "Tell me what you are working on.",
  body: "Currently seeking projects in industrial systems, operational excellence, and data-driven innovation.",
} as const;

export const contactRows: ContactRow[] = [
  { label: "Email", value: profile.email, href: `mailto:${profile.email}` },
  { label: "LinkedIn", value: profile.fullName, href: profile.linkedinUrl },
  { label: "Based in", value: profile.location },
];
